const adminRecordUrl = (packageId, resource) =>
  `${
    process.env.REACT_APP_USER_API_V2_BASEURL
  }/admin/records/${encodeURIComponent(packageId)}/${resource}`.replace(
    '/v1/',
    '/'
  )

const ADMIN_RECORD_TIMEOUT_MS = 15000
const CAPTURE_VIDEO_TIMEOUT_MS = 60000
const CAPTURE_VIDEO_FETCH_ATTEMPTS = 3

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))

const requestAdminRecord = async (
  packageId,
  resource,
  options,
  fetchImpl = window.fetch,
  errorLabel = resource,
  timeoutMs = ADMIN_RECORD_TIMEOUT_MS
) => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetchImpl(adminRecordUrl(packageId, resource), {
      ...options,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${window.localStorage.getItem('token')}`,
        ...options.headers,
      },
      signal: controller.signal,
    })

    if (!response.ok) {
      throw new Error(`${errorLabel} unavailable (${response.status})`)
    }

    return await response.json()
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export const fetchDiagnosticAssets = (packageId, fetchImpl = window.fetch) =>
  requestAdminRecord(
    packageId,
    'diagnostic-assets',
    { method: 'GET' },
    fetchImpl,
    'Diagnostic assets'
  )

/**
 * Load capture video through the platform-api proxy (bearer auth → blob URL).
 * Falls back to the Prism signed URL from diagnostic-assets when the proxy
 * cannot return bytes (CDN/size issues). Retries transient failures.
 *
 * @returns {{ url: string, revoke?: () => void } | null}
 */
export const loadCaptureVideo = async (
  packageId,
  fetchImpl = window.fetch,
  { forceRefresh: _forceRefresh = false } = {}
) => {
  let lastError

  for (let attempt = 0; attempt < CAPTURE_VIDEO_FETCH_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await sleep(350 * attempt)
    }

    // Prefer authenticated proxy so <video> does not depend on CDN CORS/403.
    try {
      const controller = new AbortController()
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        CAPTURE_VIDEO_TIMEOUT_MS
      )
      try {
        const response = await fetchImpl(
          adminRecordUrl(packageId, 'capture-video'),
          {
            method: 'GET',
            headers: {
              Accept: 'video/mp4',
              Authorization: `Bearer ${window.localStorage.getItem('token')}`,
            },
            signal: controller.signal,
          }
        )

        if (response.status === 404) {
          return null
        }

        if (response.ok) {
          const blob = await response.blob()
          if (blob && blob.size > 0) {
            const objectUrl = URL.createObjectURL(blob)
            return {
              url: objectUrl,
              revoke: () => URL.revokeObjectURL(objectUrl),
            }
          }
        }

        lastError = new Error(
          `Capture video proxy unavailable (${response.status})`
        )
      } finally {
        window.clearTimeout(timeoutId)
      }
    } catch (error) {
      lastError = error
    }

    // Fallback: signed Prism URL (works when CDN allows the browser).
    try {
      const assets = await fetchDiagnosticAssets(packageId, fetchImpl)
      const url = assets?.captureVideo?.url || null
      if (url) return { url }
      return null
    } catch (error) {
      lastError = error
    }
  }

  if (lastError) throw lastError
  return null
}

export const fetchFailureType = (packageId, fetchImpl = window.fetch) =>
  requestAdminRecord(packageId, 'failure-type', { method: 'GET' }, fetchImpl)

export const updateFailureType = (
  packageId,
  failureType,
  fetchImpl = window.fetch
) =>
  requestAdminRecord(
    packageId,
    'failure-type',
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ failureType }),
    },
    fetchImpl
  )

const unlabeledFailuresUrl = () =>
  `${process.env.REACT_APP_USER_API_V2_BASEURL}/admin/records/unlabeled-failures`.replace(
    '/v1/',
    '/'
  )

export const fetchUnlabeledFailures = (
  { limit = 20, scope = 'errors' } = {},
  fetchImpl = window.fetch
) => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    ADMIN_RECORD_TIMEOUT_MS
  )
  const params = new URLSearchParams({
    limit: String(limit),
    scope,
  })
  const url = `${unlabeledFailuresUrl()}?${params}`

  return fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${window.localStorage.getItem('token')}`,
    },
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Unlabeled failures unavailable (${response.status})`)
      }
      return response.json()
    })
    .finally(() => {
      window.clearTimeout(timeoutId)
    })
}
